import { Card, Icon, IconButton, Tooltip } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { createSelector } from 'reselect';
import { IRootState } from '../../../@types/redux';
import AppFunctionsService from '../../../services/appFunctionServices';
import GeneralFunctions from '../../../store/general/functions';
import DiseaseFunctions from '../../../store/masterData/disease/functions';
import { IDisease } from '../../../types/model/masterData/disease';
import StandardFab from '../../customComponents/button/StandardFab';
import MaterialTable from '../../customComponents/materialTable/Table';
import DiseaseEditDialog from './dialog/Edit';

interface IDiseaseListProps extends RouteComponentProps {
    diseases : Array<IDisease>;
    isLoading : boolean;
}

interface IDiseaseListState {
    selected : IDisease |null;
}

class DiseaseListComponent extends React.PureComponent<IDiseaseListProps, IDiseaseListState> {
    constructor(props : IDiseaseListProps) {
        super(props);
        this.state = {
            selected: null,
        };
    }

    public readonly componentDidMount = () => {
        this.load();
    };

    public readonly load = () => {
        DiseaseFunctions.getList();
    };

    private readonly onAddClick = () => {
        this.setState({
            selected: {
                name: '',
                isActive: true,
                localImages: [] as Array<{ name : string; path : string }>,
            } as IDisease,
        });
    };

    private readonly getData = (props : IDiseaseListProps) => props.diseases;

    private readonly getDiseases = createSelector(
        [this.getData],
        (diseases) => diseases.sort((a, b) => a.name.localeCompare(b.name)),
    );

    private save = async (disease : IDisease) => {
        try {
            await DiseaseFunctions.save(disease);

            this.setState({
                selected: null,
            });
        } catch (ex) {
            GeneralFunctions.generalShowErrorSnackbar('Error saving disease.', ex);
        }
    };

    private readonly onEditClose = (disease : IDisease | null) => {
        if (disease) {
            this.save(disease);
            return;
        }

        this.setState({
            selected: null,
        });
    };

    private readonly onRowClick = (e : React.MouseEvent<HTMLButtonElement>) => {
        const disease = this.props.diseases.find(x => x.id === e.currentTarget.value);

        if (!disease) return;

        this.setState({
            selected: {
                ...disease,
            },
        });
    };

    public render = () => {
        const { selected } = this.state;
        const { isLoading } = this.props;
        const diseases = this.getDiseases(this.props);

        return (
            <div className={'fdc hfill mh0 mw0'}>
                <div className={'flx1 fdc ml15 mr15 mt20'}>
                    <Card className={'flx1 fdc mb90'}>
                        <MaterialTable<IDisease>
                            id='diseaseTable'
                            data={diseases}
                            rowsPerPage={50}
                            isLoading={isLoading}
                            columns={[{
                                header: '',
                                paddingRight: 4,
                                width: 25,
                                renderCell: row => (
                                    <div className='aic'>
                                        <Tooltip title='Edit'>
                                            <div>
                                                <IconButton value={row.id} color='primary' onClick={this.onRowClick} aria-label='info' disabled={isLoading}>
                                                    <Icon>edit</Icon>
                                                </IconButton>
                                            </div>
                                        </Tooltip>
                                    </div>
                                ),
                            }, {
                                paddingRight: 4,
                                header: 'Name',
                                field: 'name',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Created By',
                                field: 'createdByName',
                                width: 175,
                                enableSort: true,
                                enableFilter: true,
                                renderCell: row => (
                                    <Tooltip title={AppFunctionsService.formatDateTime(row.createdOn)}>
                                        <span>{row.createdByName}</span>
                                    </Tooltip>),
                            }, {
                                header: 'Updated By',
                                field: 'updatedByName',
                                width: 175,
                                enableSort: true,
                                enableFilter: true,
                                renderCell: row => (
                                    <Tooltip title={AppFunctionsService.formatDateTime(row.updatedOn)}>
                                        <span>{row.updatedByName}</span>
                                    </Tooltip>),
                            }, {
                                header: 'Is Active?',
                                field: 'isActive',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                                renderCell: n => !n.isActive ? (<Icon className='cpr'>close</Icon>) : (<Icon className='cdg'>check</Icon>),
                            }]}
                        />
                    </Card>
                    <DiseaseEditDialog value={selected} fullWidth maxWidth='md' onClose={this.onEditClose} isLoading={isLoading} />
                    <StandardFab aria-label='add' disabled={isLoading} onClick={this.onAddClick}>
                        <AddIcon />
                    </StandardFab>
                </div>
            </div>
        );
    };
}

const mapStateToProps = (state : IRootState) => {
    return {
        diseases: state.masterData.disease.diseases,
        isLoading: state.masterData.disease.isLoading,
    };
};

const DiseaseList = connect(
    mapStateToProps,
)(DiseaseListComponent);

export default DiseaseList;